import * as types from './mutation-types.js'
import podcasts from '@/services/api/podcasts.js'
import EventBus from '@/services/bus'
import api from '@/services/api'
import localStorageService from '@/services/localStorage'
import { getDeepProperty } from '@/helpers/deepObject.js'

const state = {
  activePodcastId: {},
  // Browsers have a limit on localstorage size and we need to
  // retain the file information between component views when
  // creating a new show.
  cadence: null,
  files: [],
  keywordsSeach: [],
  plans: [],
  podcastCreationKeys: {},
  podcasts: [],
  podcast: {}, // used to store podcast info
  podcastCount: '-',
  podcastUserPermissions: {},
  recentPodcastIds: [],
  recentPodcasts: []
}

const actions = {
  clearPodcasts ({state, commit}) {
    commit(types.CLEAR_PODCASTS)
  },
  async createPodcast ({ state, commit }, {payload}) {
    let podcast = await podcasts.createPodcast(payload)

    return podcast
  },
  async createPodcastEpisodeAudio ({ state, commit }, {podcast, filename}) {
    // Get the creation endpoint
    let audio = await api.get.data(podcast.episodes.audio)

    // Create and return
    return api.post.data(audio, {file_name: filename})
  },
  async updatePodcastEpisodeAudio ({ state, commit }, {audio, payload}) {
    // Update and return
    return api.put.data(audio, payload)
  },
  async getPodcastEpisodeAudio ({ state, commit }, {audio}) {
    // Update and return
    return api.get.data(audio)
  },
  async createPodcastSeason ({ state, commit }, {podcastId, number}) {
    return podcasts.createSeason(podcastId, number)
  },
  async deletePodcast ({state, commit}, {userId, podcastId, feeds, redirect}) {
    if (feeds && redirect) {
      // Update primary feed with the redirect supplied before deleting
      const primaryFeed = feeds.collection.find((f) => f.is_primary === true)
      const updateRedirectPath = getDeepProperty(primaryFeed, 'update.action')
      if (updateRedirectPath) {
        await api.post(updateRedirectPath, {redirect_url: redirect})
      }
    }
    await podcasts.deletePodcast(podcastId)

    commit(types.REMOVE_FROM_RECENT_PODCAST_IDS, {userId, podcastId})
    commit(types.CLEAR_ACTIVE_PODCAST)
    return true
  },
  async getPodcastCreationKeys ({state, commit}, {accountId, path}) {
    if (state.podcastCreationKeys[accountId]) return

    let keys = await podcasts.getCreationKeys(path)

    commit(types.SET_PODCAST_CREATION_KEYS, {accountId, keys})
    return keys
  },
  async getPodcasts ({state, commit}, options) {
    const limit = options.limit || 10
    options = { ...options, limit: limit }

    let result = await podcasts.getPodcasts(options)
    let podcastList = result.collection
    let podcastCount = result.count

    commit(types.SET_PODCAST_COUNT, podcastCount)
    commit(types.SET_PODCASTS, podcastList)

    if (podcastList.length < options.limit) {
      return { hasMore: false, hasResults: podcastCount > 0 }
    } else {
      return { hasMore: true, hasResults: podcastCount > 0, firstId: podcastCount > 0 ? podcastList[0].id : null }
    }
  },
  async getPodcastPlans ({state, commit}) {
    let plans = await podcasts.getPlans()

    commit(types.SET_PODCAST_PLANS, plans)
  },
  async getPodcastUserPermissions ({state, commit}, userId) {
    const activePodcastId = state.activePodcastId[userId]
    if (!activePodcastId) return
    const activePodcast = state.podcast[activePodcastId]
    if (!activePodcast || !activePodcast.user_permissions) return

    let podcastUserPermissions = await podcasts.getPodcastUserPermissions(`${activePodcast.user_permissions.href}/${userId}`)

    commit(types.SET_PODCAST_USER_PERMISSIONS, podcastUserPermissions)

    return podcastUserPermissions
  },
  async getRecentPodcasts ({state, commit}, userId) {
    let recentPodcastIds = state.recentPodcastIds[userId] || []
    let recentPodcasts = await podcasts.getRecentPodcasts(recentPodcastIds)

    if (recentPodcastIds.length < 7) {
      let morePodcasts = await podcasts.getPodcasts({limit: 7, sort: 'updated_at_desc'})

      commit(types.SET_PODCAST_COUNT, morePodcasts.count)

      let filteredPodcasts = filterRecentPodcasts(recentPodcasts, morePodcasts.collection)

      filteredPodcasts.forEach((podcast) => {
        if (recentPodcasts.length < 7) {
          recentPodcasts.push(podcast)
        }
      })
    }

    commit(types.SET_RECENT_PODCASTS, {userId, recentPodcasts})
  },
  async importPodcast ({commit}, {account, plan, rssFeed}) {
    let podcast = await podcasts.importPodcast({account, plan, rssFeed})

    return podcast
  },
  // Refresh will always load the podcast again, whereas
  // setActivePodcast will only load again if it's changed
  async refreshActivePodcast ({ state, commit }, {userId, podcastId}) {
    if (!podcastId) { return }

    let activePodcast = await api.get.data(`/podcasts/${podcastId}`)
    this.dispatch('clearEpisodes')
    if (!activePodcast || !activePodcast.user_permissions) {
      removeActivePodcastData(commit, {userId, podcastId})
      return
    }

    return activatePodcast(commit, activePodcast)
  },
  async sendExitSurvey ({state, commit}, {podcastId, surveyResponse}) {
    await podcasts.sendExitSurvey(podcastId, surveyResponse)
  },
  async setActivePodcast ({ state, commit }, {userId, podcastId}) {
    if (!podcastId || !userId) { return }
    // Use refreshActivePodcast if you need to refresh
    // the current active podcast
    const activePodcastId = state.activePodcastId[userId]
    if (podcastId === activePodcastId) { return }

    this.dispatch('clearEpisodes')
    removeActivePodcastData(commit, {userId, podcastId})

    let activePodcast = await api.get.data(`/podcasts/${podcastId}`)
      .catch((e) => {
        return null
      })
    if (!activePodcast || !activePodcast.user_permissions) {
      commit(types.REMOVE_FROM_RECENT_PODCAST_IDS, {userId, podcastId})
      EventBus.$emit('SHOW_ERROR_MESSAGE', {
        key: 'failed-active-podcast-request',
        error: 'You don\'t have permission to view the requested podcast.'
      })
      return
    }

    commit(types.SET_ACTIVE_PODCAST_ID, {userId, podcastId})
    commit(types.ADD_TO_RECENT_PODCAST_IDS, {userId, podcastId})

    return activatePodcast(commit, activePodcast)
  },
  setFiles ({commit}, files) {
    commit(types.SET_FILES, files)
  },
  setRecentPodcastIds ({state, commit}, {userId, recentPodcastIds}) {
    commit(types.SET_RECENT_PODCAST_IDS, {userId, recentPodcastIds})
  },
  setRecastColor ({ state, commit }, colors) {
    commit(types.SET_RECAST_COLOR, colors)
  },

  async createAuthor ({ commit }, {podcast, name}) {
    await podcasts.createAuthor(podcast.authors.create.action, {name})
  },
  async removeAuthor ({ commit }, {podcastId, authorId}) {
    await podcasts.removeAuthor({podcastId, authorId})
  },
  async updateAuthor ({ commit }, {podcast, name}) {
    // TODO: add support for multiple authors as we're only
    // supporting a single author at this point
    if (podcast.authors.collection.length) {
      await podcasts.updateAuthor(podcast.authors.collection[0].href, {name})
    } else {
      await podcasts.createAuthor(podcast.authors.create.action, {name})
    }
  },
  async getVerificationCodes ({ commit, rootGetters }) {
    const podcast = getDeepProperty(rootGetters, 'activePodcast')
    const url = getDeepProperty(podcast, 'verification_codes.href')
    if (url) {
      await api.get(url).then((response) => {
        commit(types.SET_VERIFICATION_CODES, { codes: response.data, podcast })
      })
    }
  },
  async updateVerificationCodes ({ commit, rootGetters }, {deleteCodes, newCodes}) {
    const podcast = getDeepProperty(rootGetters, 'activePodcast')
    const url = getDeepProperty(podcast, 'verification_codes.href')

    // Delete
    if (deleteCodes.length > 0) {
      const deletePromises = deleteCodes.forEach((code) => {
        return api.delete(code.href)
      })
      if (deletePromises) {
        await Promise.all(deletePromises)
      }
    }

    // Add
    if (newCodes.length > 0) {
      const addPromises = newCodes.forEach((code) => {
        return api.post(url, {value: code})
      })
      if (addPromises) {
        await Promise.all(addPromises)
      }
    }
  },
  async updateCategories ({ commit }, {podcast, categories}) {
    // Clear all the categories first because this is the easiest
    // way to do this for now
    for (let key in podcast.categories.collection) {
      if (!podcast.categories.collection.hasOwnProperty(key)) continue

      let category = podcast.categories.collection[key]

      await podcasts.deleteCategories(category.href)
        .catch((e) => {
          console.error(e)
        })
    }

    if (!categories || categories === '') return true

    await podcasts.createCategories(podcast.categories.href, categories)

    return true
  },
  async updateKeywords ({ commit }, {podcast, deleteKeywords, newKeywords}) {
    // Delete
    let deletePromises = deleteKeywords.map((deleteKeywords) => {
      return podcasts.deleteKeywords(deleteKeywords.href)
    })
    await Promise.all(deletePromises)

    // Add
    let addPromises = newKeywords.map((keyword) => {
      return podcasts.createKeywords(podcast.keywords.href, keyword)
    })
    await Promise.all(addPromises)
  },
  async updateOwner ({ commit }, {podcast, name, email}) {
    if (podcast.owner.action) {
      return podcasts.createOwner(podcast.owner.action, {name, email})
    } else {
      return podcasts.updateOwner(podcast.owner.update.action, {name, email})
    }
  },
  updatePodcast ({ commit, state }, {podcastId, payload}) {
    return podcasts.updatePodcast(podcastId, payload)
  },
  async getIntroOutro ({commit}, podcast) {
    const path = getDeepProperty(podcast, 'intro_outro.href')
    const response = await api.get(path).then((r) => r.data)
    commit(types.SET_INTRO, {intro: response.intro, podcast})
    commit(types.SET_OUTRO, {outro: response.outro, podcast})
  },
  async updateIntro ({commit}, {podcast, intro}) {
    const introPath = getDeepProperty(podcast, 'intro_outro.intro.create.action') ? getDeepProperty(podcast, 'intro_outro.intro.create.action') : getDeepProperty(podcast, 'intro_outro.intro.update.action')
    const introData = await api.post(introPath, intro).then((response) => response.data)
    commit(types.SET_INTRO, {intro: introData, podcast})
  },
  async updateOutro ({commit}, {podcast, outro}) {
    const outroPath = getDeepProperty(podcast, 'intro_outro.outro.create.action') ? getDeepProperty(podcast, 'intro_outro.outro.create.action') : getDeepProperty(podcast, 'intro_outro.outro.update.action')
    const outroData = await api.post(outroPath, outro).then((response) => response.data)
    commit(types.SET_OUTRO, {outro: outroData, podcast})
  },
  async deleteIntro ({commit}, {podcast}) {
    const deletePath = getDeepProperty(podcast, 'intro_outro.intro.delete.action')
    await api.delete(deletePath)
  },
  async deleteOutro ({commit}, {podcast}) {
    const deletePath = getDeepProperty(podcast, 'intro_outro.outro.delete.action')
    await api.delete(deletePath)
  },
  clearSearchKeywords ({ commit }) {
    commit(types.CLEAR_SEARCH_KEYWORDS)
  },
  async searchKeywords ({ getters, commit }, {keyword}) {
    return api.get.data(`/keywords/search?keyword=${keyword}&account=${getters.activePodcast.account.id}`).then(data => {
      commit(types.SET_SEARCH_KEYWORDS, data.collection)
    })
  },
  clearCadence ({commit}) {
    commit(types.SET_PODCAST_CADENCE, null)
  },
  deleteCadence ({commit}, path) {
    return api.delete(path).then(data => {
      commit(types.SET_PODCAST_CADENCE, null)
      return data
    })
  },
  async getCadence ({commit}, path) {
    return api.get.data(path).then(data => {
      commit(types.SET_PODCAST_CADENCE, data)
      return data
    })
  },
  async updateCadence ({commit}, {path, cadence}) {
    return api.post(path, cadence).then(response => {
      commit(types.SET_PODCAST_CADENCE, response.data)
      return response.data
    })
  }
}

function filterRecentPodcasts (recentPodcasts, podcasts) {
  return podcasts.filter(function (podcast) {
    return recentPodcasts.filter(function (recentPodcast) {
      return recentPodcast.id === podcast.id
    }).length === 0
  })
}

const mutations = {
  [types.ADD_TO_RECENT_PODCAST_IDS] (state, {userId, podcastId}) {
    let recent = state.recentPodcastIds[userId] || []
    let index = recent.indexOf(podcastId)

    if (index > -1) {
      recent.splice(index, 1)
    }

    recent.unshift(podcastId)
    recent = recent.slice(0, 4)

    let recentPodcastIds = {}
    // Use try to avoid failing to load if recentPodcastIds is poorly formed JSON
    try {
      recentPodcastIds = localStorageService.getObject('recentPodcastIds') || {}
    } catch (e) {}
    // Fixes backward compatibility where the user's recentPodcastIds don't get set
    if (Array.isArray(recentPodcastIds)) {
      recentPodcastIds = {}
    }

    recentPodcastIds[userId] = recent
    localStorageService.setObject('recentPodcastIds', recentPodcastIds)
    state.recentPodcastIds = { ...state.recentPodcastIds, [userId]: recent }
  },
  [types.REMOVE_FROM_RECENT_PODCAST_IDS] (state, {userId, podcastId}) {
    let recent = state.recentPodcastIds[userId] || []
    let index = recent.indexOf(podcastId)

    recent.splice(index, 1)

    let recentPodcastIds = {}
    try {
      recentPodcastIds = localStorageService.getObject('recentPodcastIds') || {}
    } catch (e) {}
    recentPodcastIds[userId] = recent

    localStorageService.setObject('recentPodcastIds', recentPodcastIds)
    state.recentPodcastIds = { ...state.recentPodcastIds, [userId]: recent }
    state.recentPodcasts = []
  },
  [types.CLEAR_ACTIVE_PODCAST] (state) {
    state.activePodcastId = {}
    localStorageService.remove('activePodcastId')
  },
  [types.CLEAR_PODCASTS] (state) {
    state.podcasts = []
    state.podcastCount = '-'
  },
  [types.SET_ACTIVE_PODCAST_ID] (state, {userId, podcastId}) {
    localStorageService.setObject('activePodcastId', {[userId]: podcastId})
    state.activePodcastId = { ...state.activePodcastId, [userId]: podcastId }
  },
  [types.SET_ACTIVE_PODCAST] (state, podcast) {
    state.podcast = { ...state.podcast, [podcast.id]: podcast }
  },
  [types.SET_FILES] (state, files) {
    state.files = files
  },
  [types.SET_PODCASTS] (state, podcasts) {
    state.podcasts = podcasts
  },
  [types.SET_PODCAST_CREATION_KEYS] (state, {accountId, keys}) {
    state.podcastCreationKeys = { ...state.podcastCreationKeys, [accountId]: keys }
  },
  [types.SET_PODCAST_PLANS] (state, plans) {
    state.plans = plans
  },
  [types.SET_PODCAST_USER_PERMISSIONS] (state, podcastUserPermissions) {
    state.podcastUserPermissions = podcastUserPermissions
  },
  [types.SET_RECENT_PODCASTS] (state, {userId, recentPodcasts}) {
    state.recentPodcasts = { ...state.recentPodcasts, [userId]: recentPodcasts }
  },
  [types.SET_RECENT_PODCAST_IDS] (state, {userId, recentPodcastIds}) {
    state.recentPodcastIds[userId] = recentPodcastIds || []
  },
  [types.SET_RECAST_COLOR] (state, color) {
    let cast = state.podcasts.find(p => p.id === state.activePodcastId)
    cast.recast_color = color
  },
  [types.SET_PODCAST_COUNT] (state, podcastCount) {
    state.podcastCount = podcastCount
  },
  [types.SET_INTRO] (state, {intro, podcast}) {
    state.podcast[podcast.id].intro_outro.intro = intro
  },
  [types.SET_OUTRO] (state, {outro, podcast}) {
    state.podcast[podcast.id].intro_outro.outro = outro
  },
  [types.SET_VERIFICATION_CODES] (state, { codes, podcast }) {
    state.podcast[podcast.id].verification_codes = codes
  },
  [types.CLEAR_SEARCH_KEYWORDS] (state) {
    state.keywordsSeach = []
  },
  [types.SET_SEARCH_KEYWORDS] (state, keywords) {
    state.keywordsSeach = keywords
  },
  [types.SET_PODCAST_CADENCE] (state, cadence) {
    state.cadence = cadence
  }
}
const getters = {
  activePodcast: (state, rootState) => {
    if (!rootState.user || !rootState.user.id || !state.activePodcastId[rootState.user.id]) return {}
    const activePodcastId = state.activePodcastId[rootState.user.id]
    if (!activePodcastId || !state.podcast || !state.podcast[activePodcastId]) return {}
    return state.podcast[activePodcastId]
  },
  activePodcastId: (state, rootState) => {
    if (!rootState.user || !rootState.user.id || !state.activePodcastId[rootState.user.id]) return null

    return state.activePodcastId[rootState.user.id]
  },
  activePodcastFeatures: (state, rootState) => (getters.activePodcast(state, rootState) || {}).features || {},
  activePodcastUserPermissions: state => state.podcastUserPermissions,
  podcastCreationKeys: (state, rootState) => {
    if (!rootState.currentAccount || !rootState.currentAccount.id || !state.podcastCreationKeys[rootState.currentAccount.id]) return false

    return state.podcastCreationKeys[rootState.currentAccount.id]
  },
  podcastFiles: state => state.files,
  podcastPlans: state => state.plans,
  podcasts: state => state.podcasts,
  podcast: state => state.podcast,
  recentPodcasts: (state, rootState) => {
    if (!rootState.user || !rootState.user.id || !state.recentPodcasts[rootState.user.id]) return false

    return state.recentPodcasts[rootState.user.id]
  },
  podcastCount: state => state.podcastCount,
  keywordsSeach: state => state.keywordsSeach,
  podcastCadence: state => state.cadence
}

function removeActivePodcastData (commit, {userId, podcastId}) {
  commit(types.CLEAR_ACTIVE_PODCAST)
  commit(types.CLEAR_PODCASTS)
  commit(types.REMOVE_FROM_RECENT_PODCAST_IDS, {userId, podcastId})
}

async function activatePodcast (commit, podcast) {
  if (!podcast) return

  podcast.subscription_item = await api.get.data(podcast.subscription_item)
  if (!podcast.subscription_item) {
    console.error('There is no subscription item for podcast id ', podcast.id)
  } else {
    if (podcast.subscription_item.plan) {
      podcast.subscription_item.plan = podcast.plan = await api.get.data(podcast.subscription_item.plan)
    }
    if (podcast.subscription_item.features) {
      podcast.subscription_item.features = podcast.features = await api.get.data(podcast.subscription_item.features)
    }
  }

  commit(types.SET_ACTIVE_PODCAST, podcast)

  return podcast
}

export default {
  actions,
  getters,
  mutations,
  state
}
